import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Check } from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import MobileStickyCTA from "@/components/site/MobileStickyCTA";
import QuoteContact from "@/components/site/QuoteContact";

const CANONICAL = "https://mlexteriordesign.com/services";

// One section per Google Business Profile category — the H2 uses the exact
// category phrasing so audit tools can match "Matching Page" per category.
const CATEGORIES = [
  {
    id: "flooring-contractor",
    heading: "Flooring Contractor",
    tagline: "Our primary trade — St. Louis & St. Charles County",
    copy: "M&L is a full-service flooring contractor covering the entire Greater St. Louis metro. LVP, laminate, tile, flooring removal, subfloor prep, and baseboards — every step handled by the same owner-operated crew.",
    image: "https://customer-assets.emergentagent.com/job_hardscape-pro-5/artifacts/ud9ci9kn_2F372B81-94D2-480F-B9D3-8B7BE4993A4D.png",
    bullets: [
      "LVP, laminate & tile installation",
      "Level subfloor prep + moisture barrier",
      "Complete tear-out and haul-away",
      "Baseboard & trim finishing",
    ],
  },
  {
    id: "wood-floor-installation-service",
    heading: "Wood Floor Installation Service",
    tagline: "Wood-look LVP, laminate, and engineered plank",
    copy: "Wide-plank wood-look flooring is our most-requested finish. We install engineered hardwood-look laminate and premium wood-look LVP across St. Louis and St. Charles County homes.",
    image: "https://customer-assets-eiarnc6j.emergentagent.net/job_hardscape-pro-5/artifacts/4wknacka_IMG_8732.jpeg",
    bullets: [
      "Wide-plank & standard-width wood-look",
      "Espresso, weathered oak, natural, gray",
      "Kitchens · living rooms · basements",
      "Precision cuts at every doorway & transition",
    ],
  },
  {
    id: "wood-and-laminate-flooring-supplier",
    heading: "Wood and Laminate Flooring Supplier",
    tagline: "Materials + install · 20% material discount",
    copy: "Ask about our 20% discount on materials when you book your install through M&L. We source premium laminate and wood-look LVP direct from top manufacturers and pass the savings to you.",
    image: "https://customer-assets-eiarnc6j.emergentagent.net/job_hardscape-pro-5/artifacts/paytz0q5_IMG_8223.jpeg",
    bullets: [
      "20% material discount on booked installs",
      "Wide range of laminate finishes",
      "Waterproof LVP click-lock & glue-down",
      "Underlayment & moisture barrier included",
    ],
  },
  {
    id: "tile-contractor",
    heading: "Tile Contractor",
    tagline: "Bathrooms · kitchens · custom patterns",
    copy: "As a St. Louis tile contractor we handle everything from marble-look large-format porcelain to hex mosaics and herringbone. Every cut scored on-site for tight grout lines and flush thresholds.",
    image: "https://customer-assets.emergentagent.com/job_hardscape-pro-5/artifacts/9z6zytzm_IMG_7768.jpeg",
    bullets: [
      "Ceramic · porcelain · natural stone",
      "Hex · marble-look · herringbone · mosaic",
      "Bathroom floors, walls & shower niches",
      "Kitchen backsplash & entryway tile",
    ],
  },
  {
    id: "bathroom-remodeler",
    heading: "Bathroom Remodeler",
    tagline: "Bathroom flooring & tile remodels",
    copy: "We specialize in the flooring side of bathroom remodels — full-floor tile, waterproof shower walls, marble-look primary baths, and waterproof LVP for cost-effective refreshes.",
    image: "https://customer-assets-eiarnc6j.emergentagent.net/job_hardscape-pro-5/artifacts/x7m4cm17_IMG_8219.jpeg",
    bullets: [
      "Full bathroom tile installation",
      "Waterproof shower walls & niches",
      "Freestanding tub surrounds",
      "Waterproof LVP bathroom floors",
    ],
  },
  {
    id: "kitchen-remodeler",
    heading: "Kitchen Remodeler",
    tagline: "Kitchen flooring & backsplash tile",
    copy: "New floors and backsplash tile bring the biggest transformation in a kitchen remodel. We install LVP, laminate, large-format tile floors, and custom backsplashes.",
    image: "https://customer-assets-eiarnc6j.emergentagent.net/job_hardscape-pro-5/artifacts/7satbct7_IMG_8391.jpeg",
    bullets: [
      "Waterproof LVP kitchen floors",
      "Large-format & marble-look tile",
      "Kitchen backsplash tile installation",
      "Perfect transitions at appliances",
    ],
  },
  {
    id: "interior-designer",
    heading: "Interior Designer",
    tagline: "Material selection & layout guidance",
    copy: "We help homeowners choose the right flooring finish, tile pattern, and grout to match their home — from modern white-oak LVP to timeless herringbone marble tile.",
    image: "https://customer-assets-eiarnc6j.emergentagent.net/job_hardscape-pro-5/artifacts/bd1eb7qe_IMG_8258.jpeg",
    bullets: [
      "Material & finish selection consultation",
      "Layout & pattern recommendations",
      "Grout color matching",
      "Wall / floor material coordination",
    ],
  },
  {
    id: "remodeler",
    heading: "Remodeler",
    tagline: "Whole-home flooring transformations",
    copy: "From single-room refreshes to whole-home main-level flooring remodels — same crew, same standard, same guarantee. We handle every square foot from tear-out to trim.",
    image: "https://customer-assets-eiarnc6j.emergentagent.net/job_hardscape-pro-5/artifacts/ipk2asyx_IMG_8221.jpeg",
    bullets: [
      "Whole-home floor replacement",
      "Multi-room LVP or laminate installs",
      "Transition strips between rooms",
      "Baseboard & finish trim included",
    ],
  },
  {
    id: "home-builder",
    heading: "Home Builder Flooring Partner",
    tagline: "New-build flooring for St. Charles County",
    copy: "We partner with builders and homeowners on new-construction flooring — coordinating install timing around trim, cabinets, and appliance delivery for a clean finish.",
    image: "https://customer-assets-eiarnc6j.emergentagent.net/job_hardscape-pro-5/artifacts/xo198ivi_IMG_8222.jpeg",
    bullets: [
      "New-construction flooring installs",
      "Coordination with GC & trades",
      "Full main-level LVP or laminate",
      "Bath & kitchen tile packages",
    ],
  },
  {
    id: "contractor",
    heading: "Licensed & Insured Contractor",
    tagline: "Missouri contractor · Est. 2013",
    copy: "M&L Exterior and Interior Design is a fully licensed and insured Missouri contractor, family-owned and based in O'Fallon since 2013. Insurance certificate available on request.",
    image: "https://customer-assets-eiarnc6j.emergentagent.net/job_hardscape-pro-5/artifacts/oo6v3oj1_IMG_8220.jpeg",
    bullets: [
      "Fully licensed in Missouri",
      "General liability & workers' comp insurance",
      "Owner-operated since 2013",
      "5.0 Google rating",
    ],
  },
];

export default function Services() {
  useEffect(() => {
    const prevTitle = document.title;
    const prevDesc = document
      .querySelector('meta[name="description"]')
      ?.getAttribute("content");
    const prevCanonical = document
      .querySelector('link[rel="canonical"]')
      ?.getAttribute("href");

    document.title =
      "All Services · Flooring Contractor St. Louis | M&L";

    const desc = document.querySelector('meta[name="description"]');
    if (desc) {
      desc.setAttribute(
        "content",
        "Complete list of M&L services in St. Louis & St. Charles County — Flooring Contractor, Wood Floor Installation, Wood and Laminate Flooring Supplier, Tile Contractor, Bathroom Remodeler, Kitchen Remodeler, Interior Designer, Remodeler, Home Builder Flooring Partner, Licensed Contractor."
      );
    }

    const canonicalEl = document.querySelector('link[rel="canonical"]');
    if (canonicalEl) canonicalEl.setAttribute("href", CANONICAL);

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "services-hub-schema";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "ItemList",
      "@id": `${CANONICAL}#services`,
      name: "M&L Exterior and Interior Design — Services",
      itemListElement: CATEGORIES.map((c, i) => ({
        "@type": "ListItem",
        position: i + 1,
        item: {
          "@type": "Service",
          "@id": `${CANONICAL}#${c.id}`,
          name: c.heading,
          description: c.copy,
          provider: { "@id": "https://mlexteriordesign.com/#business" },
          areaServed: [
            { "@type": "AdministrativeArea", name: "St. Louis County, Missouri" },
            { "@type": "AdministrativeArea", name: "St. Charles County, Missouri" },
            { "@type": "City", name: "St. Louis, Missouri" },
          ],
        },
      })),
    });
    document.head.appendChild(script);

    window.scrollTo(0, 0);

    return () => {
      document.title = prevTitle;
      if (desc && prevDesc) desc.setAttribute("content", prevDesc);
      if (canonicalEl && prevCanonical)
        canonicalEl.setAttribute("href", prevCanonical);
      const s = document.getElementById("services-hub-schema");
      if (s) s.remove();
    };
  }, []);

  return (
    <div
      className="min-h-screen bg-stone-50 text-stone-900"
      data-testid="services-hub-page"
    >
      <Header />

      <main>
        {/* HERO */}
        <section className="relative pt-32 pb-14 lg:pt-40 lg:pb-20 bg-stone-900 text-stone-50 overflow-hidden">
          <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
            <nav aria-label="Breadcrumb" className="text-xs text-stone-400 mb-6">
              <Link to="/" className="hover:text-orange-400 transition-colors">
                Home
              </Link>{" "}
              <span className="text-stone-600">/</span>{" "}
              <span className="text-stone-200">Services</span>
            </nav>

            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-orange-400 font-semibold mb-6">
              <MapPin size={13} />
              <span>Serving St. Louis · St. Charles County · Missouri</span>
            </div>

            <h1
              data-testid="services-h1"
              className="font-display font-semibold text-4xl md:text-6xl lg:text-7xl leading-[1.02] tracking-[-0.03em] max-w-4xl"
            >
              All Services
              <br />
              <span className="text-orange-400">M&L Exterior and Interior Design</span>
            </h1>

            <p className="mt-6 text-lg lg:text-xl text-stone-300 leading-relaxed max-w-2xl">
              Everything we do in one place — Flooring Contractor · Wood Floor
              Installation · Tile Contractor · Bathroom Remodeler · Kitchen
              Remodeler · Interior Designer · Remodeler · and more.
            </p>

            {/* Quick-jump chips */}
            <nav
              aria-label="Services jump menu"
              data-testid="services-nav"
              className="mt-10 flex flex-wrap gap-2"
            >
              {CATEGORIES.map((c) => (
                <a
                  key={c.id}
                  href={`#${c.id}`}
                  data-testid={`services-jump-${c.id}`}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-stone-200 bg-stone-50/10 hover:bg-orange-500 hover:text-stone-50 border border-stone-50/20 rounded-full px-3.5 py-1.5 transition-colors"
                >
                  {c.heading}
                </a>
              ))}
            </nav>
          </div>
        </section>

        {/* CATEGORY SECTIONS */}
        <section className="py-16 lg:py-24 bg-white">
          <div className="max-w-[1200px] mx-auto px-6 lg:px-10 space-y-16 lg:space-y-24">
            {CATEGORIES.map((c, i) => (
              <article
                key={c.id}
                id={c.id}
                data-testid={`services-cat-${c.id}`}
                className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 items-center scroll-mt-28"
              >
                <div
                  className={`lg:col-span-6 ${i % 2 === 1 ? "lg:order-2" : ""}`}
                >
                  <div className="relative aspect-[4/3] overflow-hidden rounded-3xl bg-stone-200 shadow-[0_20px_60px_-25px_rgba(0,0,0,0.3)]">
                    <img
                      src={c.image}
                      alt={`${c.heading} services in St. Louis & St. Charles County by M&L Exterior and Interior Design`}
                      loading="lazy"
                      className="absolute inset-0 h-full w-full object-cover"
                    />
                  </div>
                </div>

                <div className="lg:col-span-6">
                  <div className="text-xs uppercase tracking-[0.15em] text-orange-600 font-semibold mb-3">
                    Service · 0{i + 1}
                  </div>
                  <h2
                    data-testid={`services-cat-h2-${c.id}`}
                    className="font-display font-semibold text-3xl md:text-4xl lg:text-[42px] leading-[1.08] tracking-[-0.02em] text-stone-900"
                  >
                    {c.heading}
                  </h2>
                  <div className="mt-2 text-lg lg:text-xl text-stone-500">
                    in St. Louis &amp; St. Charles County, Missouri
                  </div>
                  <p className="mt-3 text-sm font-semibold text-orange-600 tracking-wide">
                    {c.tagline}
                  </p>
                  <p className="mt-5 text-stone-700 text-[16px] leading-[1.75]">
                    {c.copy}
                  </p>

                  <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                    {c.bullets.map((b) => (
                      <li
                        key={b}
                        className="flex items-start gap-2.5 text-sm text-stone-800"
                      >
                        <Check
                          size={16}
                          strokeWidth={2.5}
                          className="text-orange-600 mt-0.5 shrink-0"
                        />
                        {b}
                      </li>
                    ))}
                  </ul>

                  <a
                    href="#contact"
                    data-testid={`services-cta-${c.id}`}
                    className="mt-7 inline-flex items-center gap-2 px-6 py-3.5 bg-stone-900 text-stone-50 text-sm font-semibold rounded-full hover:bg-orange-600 transition-colors group"
                  >
                    Get a Quote
                    <ArrowRight
                      size={15}
                      className="group-hover:translate-x-0.5 transition-transform"
                    />
                  </a>
                </div>
              </article>
            ))}
          </div>
        </section>

        <QuoteContact />
      </main>

      <Footer />
      <MobileStickyCTA />
    </div>
  );
}
